import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Col,
  Row,
  Form,
  Input,
  Button,
  Checkbox,
  Typography,
  Image,
  message,
} from "antd";
import { CheckOutlined } from "@ant-design/icons";
import { LoginWrapper } from "./Login.style";
import Logo from "./../../assets/logo.svg";
import lgSlider from "./../../assets/lg-slider-1.png";
import { login as authLogin } from "./../../services/authService";

const { Paragraph, Link } = Typography;

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const onFinish = async () => {
    if (!email || !password) {
      setError("Username and password are required");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await authLogin(email, password);
      
      if (!response) {
        message.error("Unable to connect to server. Please try again later.");
        return;
      }
      
      const data = await response.json();

      if (response.ok) {
        if (!data.user || !data.token) {
          message.error("Something went wrong. Please try again.");
          return;
        }
        
        localStorage.setItem("authToken", data.token);
        localStorage.setItem("userRole", data.user.role);
        localStorage.setItem("userName", data.user.name);
        localStorage.setItem("userEmail", data.user.email);
        localStorage.setItem("userOrganization", data.user.organization || "");
        localStorage.setItem("userIsActive", data.user.isActive ? "true" : "false");
        
        message.success("Login successful!");
        
        const redirectRoute = data.redirectRoute;
        if (redirectRoute) {
          navigate(redirectRoute);
        } else {
          const userRole = data.user.role?.toLowerCase();
          if (userRole === "customer") {
            navigate("/customer");
          } else if (userRole === "partner") {
            navigate("/partner");
          } else {
            navigate("/admin");
          }
        }
      } else {
        message.error("Invalid credentials. Please check your email and password.");
      }
    } catch (err) {
      if (err.name === "TypeError" && err.message.includes("fetch")) {
        message.error("Unable to connect to server. Please check your connection and try again.");
      } else {
        message.error("Something went wrong. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  return (
    <LoginWrapper>
      <Row className="login-row">
        <Col span={12} md={12} sm={0} xs={0} className="left-panel">
          <div className="logo-section">
            <Image width={160} preview={false} src={Logo} />
          </div>
          
          <div className="hero-section">
            <h1 className="headline">
              Scale Your Tech Team <span className="highlight">FASTER</span>
            </h1>
            <Paragraph className="tagline">
              Top 1% vetted Tech professionals - ready to augment your team and build your NEXT BIG THING!
            </Paragraph>
            
            <div className="feature-badges">
              <div className="badge">
                <span className="check-icon"><CheckOutlined /></span>
                AI Matched Talents
              </div>
              <div className="badge">
                <span className="check-icon"><CheckOutlined /></span>
                BGV & Skill Verified
              </div>
              <div className="badge">
                <span className="check-icon"><CheckOutlined /></span>
                Enterprise Ready
              </div>
            </div>
          </div>

          <div className="product-image-section">
            <Image preview={false} src={lgSlider} className="product-image" />
          </div>

          <div className="trusted-section">
            <div className="trusted-label">TRUSTED BY</div>
            <div className="trusted-text">Top Enterprises, Leading Startups and other Venture Capital Firms</div>
            <div className="testimonial">
              <div className="quote">"HireNXT helped us close senior roles in 5 days."</div>
              <div className="author">- CTO, FinTech Startup</div>
            </div>
          </div>
        </Col>

        <Col span={12} md={12} sm={24} xs={24} className="right-panel">
          <div className="tech-pattern" />
          <div className="glass-sphere-1" />
          <div className="glass-sphere-2" />
          <div className="glass-sphere-3" />
          <div className="login-form-container">
            <h1 className="welcome-title">Hi, Welcome to HireNXT!</h1>
            <Paragraph className="welcome-subtitle">
              Start your journey to scale your Enterprise Now!
            </Paragraph>

            <Form
              name="login"
              layout="vertical"
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              autoComplete="off"
            >
              <Form.Item
                label="Email Id"
                name="username"
                rules={[
                  {
                    required: true,
                    message: "Enter Your Email Id",
                  },
                ]}
              >
                <Input 
                  placeholder="Enter Your Email Id"
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                />
              </Form.Item>

              <Form.Item
                label="Password"
                name="password"
                rules={[
                  {
                    required: true,
                    message: "Please input your password!",
                  },
                ]}
              >
                <Input.Password
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Form.Item>

              <div className="remember-forgot-row">
                <Checkbox>Remember me</Checkbox>
                <span className="forgot-link">Forgot Password?</span>
              </div>

              <Form.Item>
                <Button 
                  className="login-btn" 
                  htmlType="submit" 
                  loading={loading}
                >
                  LOGIN
                </Button>
              </Form.Item>

              <Paragraph className="signup-text">
                Don't have an account with us?{" "}
                <Link className="signup-link" onClick={() => navigate("/signup")}>
                  Sign up here
                </Link>
              </Paragraph>
            </Form>
          </div>
        </Col>
      </Row>
    </LoginWrapper>
  );
};

export default Login;
